import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { StatusBadge, UndoButton } from '@/components/common';
import type { CellRendererParams, ColDef } from '@/grid/types';
import { dateOnlyFormatter } from '@/grid/value-formatters/dateOnlyFormatter';
import type {
  OnFarmImplantApiSourceType,
  OnFarmImplantQueueEditableField,
  OnFarmImplantQueueFieldValue,
  OnFarmImplantQueueRecord,
  OnFarmImplantQueueRequiredField,
} from '@/services/on-farm-implant';

import { ClSideToggle } from './ClSideToggle';
import {
  type QueueFieldOption,
  useImplantQueueFieldOptions,
} from './fieldOptions';
import {
  UndoInputCellEditor,
  type UndoInputCellEditorParams,
} from './UndoInputCellEditor';
import type { QueueEditHistory } from './useQueueEditHistory';

const statusColors = {
  Selected: { bg: 'bg-gray-100', text: 'text-gray-700' },
  Loaded: { bg: 'bg-blue-100', text: 'text-blue-700' },
  Queued: { bg: 'bg-green-100', text: 'text-green-700' },
  Discarded: { bg: 'bg-red-100', text: 'text-red-700' },
  Frozen: { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  'On-Farm Freezing': { bg: 'bg-sky-100', text: 'text-sky-700' },
  Unknown: { bg: 'bg-gray-100', text: 'text-gray-700' },
};

const SOURCE_TYPE_COLORS: Record<
  OnFarmImplantApiSourceType,
  { bg: string; text: string }
> = {
  fresh: { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  frozen: { bg: 'bg-sky-100', text: 'text-sky-700' },
  flush: { bg: 'bg-violet-100', text: 'text-violet-700' },
  'client-on-farm': { bg: 'bg-amber-100', text: 'text-amber-800' },
  'non-tog': { bg: 'bg-slate-200', text: 'text-slate-700' },
};

export const IMPLANT_QUEUE_SOURCE_TYPES: OnFarmImplantApiSourceType[] = [
  'fresh',
  'frozen',
  'flush',
  'client-on-farm',
  'non-tog',
];

const SOURCE_TYPE_LABEL_KEYS: Record<OnFarmImplantApiSourceType, string> = {
  fresh: 'onFarmImplant.implantQueue.sourceTypes.fresh',
  frozen: 'onFarmImplant.implantQueue.sourceTypes.frozen',
  flush: 'onFarmImplant.implantQueue.sourceTypes.flush',
  'client-on-farm': 'onFarmImplant.implantQueue.sourceTypes.clientOnFarm',
  'non-tog': 'onFarmImplant.implantQueue.sourceTypes.nonTog',
};

export const useSourceTypeLabel = () => {
  const { t } = useTranslation();

  return useMemo(
    () => (sourceType: string) => {
      const key =
        SOURCE_TYPE_LABEL_KEYS[sourceType as OnFarmImplantApiSourceType];
      return key ? t(key) : sourceType;
    },
    [t],
  );
};

export const IMPLANT_QUEUE_EDITABLE_FIELDS: OnFarmImplantQueueEditableField[] =
  [
    'recipientTag',
    'implantType',
    'clSide',
    'quality',
    'distance',
    'implantTechId',
    'farmLocationId',
    'transferComment',
    'dairyCompId',
    'implantDate',
  ];

export const IMPLANT_QUEUE_FIELD_LABEL_KEYS: Record<
  OnFarmImplantQueueEditableField,
  string
> = {
  recipientTag: 'onFarmImplant.implantQueue.fields.recipientTag',
  implantType: 'onFarmImplant.implantQueue.fields.implantType',
  clSide: 'onFarmImplant.implantQueue.fields.clSide',
  quality: 'onFarmImplant.implantQueue.fields.quality',
  distance: 'onFarmImplant.implantQueue.fields.distance',
  implantTechId: 'onFarmImplant.implantQueue.fields.implantTech',
  farmLocationId: 'onFarmImplant.implantQueue.fields.farmLocation',
  transferComment: 'onFarmImplant.implantQueue.fields.transferComment',
  dairyCompId: 'onFarmImplant.implantQueue.fields.barcodeId',
  implantDate: 'onFarmImplant.implantQueue.fields.implantDate',
};

const REQUIRED_FIELD_LABEL_KEYS: Record<
  OnFarmImplantQueueRequiredField,
  string
> = {
  recipientTag: IMPLANT_QUEUE_FIELD_LABEL_KEYS.recipientTag,
  implantType: IMPLANT_QUEUE_FIELD_LABEL_KEYS.implantType,
};

export const IMPLANT_QUEUE_TEXT_LIMITS = {
  recipientTag: 25,
  transferComment: 100,
} as const;

export const IMPLANT_QUEUE_SELECT_BLANK = '';

const labelFor = <T extends string | number>(
  options: QueueFieldOption<T>[],
  value: unknown,
) => options.find((option) => option.value === value)?.label;

export const useQueueFieldFormatter = () => {
  const options = useImplantQueueFieldOptions();

  return useMemo(
    () =>
      (
        field: OnFarmImplantQueueEditableField,
        value: OnFarmImplantQueueFieldValue | undefined,
        row?: OnFarmImplantQueueRecord,
      ): string => {
        if (
          value === null ||
          value === undefined ||
          value === IMPLANT_QUEUE_SELECT_BLANK
        ) {
          return '';
        }
        switch (field) {
          case 'implantTechId':
            return (
              labelFor(options.techOptions, value) ??
              (row?.implantTechId === value ? row.implantTechName : null) ??
              String(value)
            );
          case 'farmLocationId':
            return (
              labelFor(options.farmLocationOptions, value) ??
              (row?.farmLocationId === value ? row.farmLocationName : null) ??
              String(value)
            );
          case 'implantType':
            return (
              labelFor(options.implantTypeOptions, value) ??
              (row?.implantType === value ? row.implantTypeName : null) ??
              String(value)
            );
          case 'clSide':
            return labelFor(options.clSideOptions, value) ?? String(value);
          case 'implantDate':
            return dateOnlyFormatter({ value: String(value) });
          default:
            return String(value);
        }
      },
    [options],
  );
};

type ImplantQueueColumnDefsOptions = {
  readOnly?: boolean;
  history?: QueueEditHistory;
  onRevert?: (
    row: OnFarmImplantQueueRecord,
    field: OnFarmImplantQueueEditableField,
    previous: OnFarmImplantQueueFieldValue,
  ) => void;
  onFieldChange?: (
    row: OnFarmImplantQueueRecord,
    field: OnFarmImplantQueueEditableField,
    value: OnFarmImplantQueueFieldValue,
  ) => void;
};

type QueueColumnCallbacks = Pick<
  ImplantQueueColumnDefsOptions,
  'history' | 'onRevert' | 'onFieldChange'
>;

const createQueueColumnCallbacks = () => {
  let current: QueueColumnCallbacks = {};
  return {
    get: () => current,
    set: (next: QueueColumnCallbacks) => {
      current = next;
    },
  };
};

type SelectField = 'implantTechId' | 'farmLocationId' | 'implantType';

const SELECT_NAME_FIELDS: Partial<
  Record<
    SelectField,
    'implantTechName' | 'farmLocationName' | 'implantTypeName'
  >
> = {
  implantTechId: 'implantTechName',
  farmLocationId: 'farmLocationName',
  implantType: 'implantTypeName',
};

export const useImplantQueueColumnDefs = ({
  readOnly = false,
  history,
  onRevert,
  onFieldChange,
}: ImplantQueueColumnDefsOptions = {}): ColDef<OnFarmImplantQueueRecord>[] => {
  const { t } = useTranslation();
  const sourceTypeLabel = useSourceTypeLabel();
  const options = useImplantQueueFieldOptions();
  const formatField = useQueueFieldFormatter();
  const [callbacks] = useState(createQueueColumnCallbacks);

  useEffect(() => {
    callbacks.set({ history, onRevert, onFieldChange });
  }, [callbacks, history, onRevert, onFieldChange]);

  return useMemo(() => {
    const isCellEditable = (params: { data?: OnFarmImplantQueueRecord }) =>
      !readOnly && !!params.data?.isEditable;

    const editableCellClassRules = { 'editable-cell': isCellEditable };

    const sourceTypeColors = Object.fromEntries(
      IMPLANT_QUEUE_SOURCE_TYPES.map((sourceType) => [
        sourceTypeLabel(sourceType),
        SOURCE_TYPE_COLORS[sourceType],
      ]),
    );

    const fieldHeader = (field: OnFarmImplantQueueEditableField) =>
      t(IMPLANT_QUEUE_FIELD_LABEL_KEYS[field]);

    const undoButton = (
      row: OnFarmImplantQueueRecord,
      field: OnFarmImplantQueueEditableField,
      beforeRevert?: () => void,
    ) => {
      const rowHistory = callbacks.get().history?.get(row.sessionQueueId);
      if (!rowHistory?.has(field)) return null;
      const previous = rowHistory.get(field) ?? null;
      const label =
        formatField(field, previous, row) ||
        t('onFarmImplant.implantQueue.blankValue');
      const title = t('onFarmImplant.implantQueue.revertTitle', {
        value: label,
      });
      return (
        <UndoButton
          title={title}
          disabled={readOnly || !row.isEditable}
          onUndo={() => {
            beforeRevert?.();
            callbacks.get().onRevert?.(row, field, previous);
          }}
        />
      );
    };

    const undoCellRenderer =
      (field: OnFarmImplantQueueEditableField) =>
      (params: {
        data?: OnFarmImplantQueueRecord;
        value?: unknown;
        valueFormatted?: string | null;
      }) => (
        <span className="flex w-full items-center justify-between gap-1">
          <span className="truncate">
            {params.valueFormatted ??
              (params.value as string | number | null) ??
              ''}
          </span>
          {params.data && undoButton(params.data, field)}
        </span>
      );

    const undoEditor = (
      field: OnFarmImplantQueueEditableField,
      editorParams: Omit<UndoInputCellEditorParams, 'renderUndo'>,
    ): Pick<
      ColDef<OnFarmImplantQueueRecord>,
      'cellEditor' | 'cellEditorParams' | 'cellRenderer'
    > => ({
      cellEditor: UndoInputCellEditor,
      cellEditorParams: {
        ...editorParams,
        renderUndo: (row: OnFarmImplantQueueRecord, beforeRevert: () => void) =>
          undoButton(row, field, beforeRevert),
      },
      cellRenderer: undoCellRenderer(field),
    });

    const selectColumn = (
      field: SelectField,
      selectOptions: QueueFieldOption<string | number>[],
    ): ColDef<OnFarmImplantQueueRecord> => ({
      field,
      colId: field,
      headerName: fieldHeader(field),
      initialFlex: 1,
      minWidth: 160,
      editable: isCellEditable,
      cellClassRules: editableCellClassRules,
      cellRenderer: (params: {
        data?: OnFarmImplantQueueRecord;
        value?: unknown;
        valueFormatted?: string | null;
      }) => (
        <span className="flex w-full items-center justify-between gap-1">
          <span className="truncate">
            {params.valueFormatted ?? (params.value as string | null) ?? ''}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {params.data && undoButton(params.data, field)}
            {isCellEditable(params) && (
              <i className="pi pi-chevron-down shrink-0 text-[10px] text-slate-400" />
            )}
          </span>
        </span>
      ),
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        values: [
          IMPLANT_QUEUE_SELECT_BLANK,
          ...selectOptions.map((option) => option.value),
        ],
        formatValue: (value: OnFarmImplantQueueFieldValue) =>
          value === IMPLANT_QUEUE_SELECT_BLANK
            ? t('onFarmImplant.implantQueue.none')
            : formatField(field, value),
        allowTyping: true,
        filterList: true,
        searchType: 'matchAny',
        highlightMatch: true,
      },
      valueFormatter: (params) =>
        formatField(
          field,
          params.value as OnFarmImplantQueueFieldValue | undefined,
          params.data,
        ),
      filter: 'agSetColumnFilter',
      filterValueGetter: (params: { data?: OnFarmImplantQueueRecord }) => {
        const row = params.data;
        if (!row) return null;
        const nameField = SELECT_NAME_FIELDS[field];
        if (nameField) return row[nameField] ?? null;
        const value = row[field];
        return value === null || value === undefined
          ? null
          : formatField(field, value, row);
      },
    });

    const missingLabels = (row: OnFarmImplantQueueRecord) =>
      (row.missingFields ?? []).map((field) =>
        t(REQUIRED_FIELD_LABEL_KEYS[field] ?? field),
      );

    const columns: ColDef<OnFarmImplantQueueRecord>[] = [
      {
        colId: 'select',
        headerName: '',
        width: 44,
        maxWidth: 44,
        checkboxSelection: !readOnly,
        headerCheckboxSelection: !readOnly,
        headerCheckboxSelectionFilteredOnly: true,
        suppressMovable: true,
        lockPosition: true,
        filter: false,
        sortable: false,
        resizable: false,
        suppressColumnsToolPanel: true,
      },
      {
        field: 'queueOrder',
        colId: 'queueOrder',
        headerName: t('onFarmImplant.implantQueue.queueOrder'),
        initialWidth: 90,
        minWidth: 80,
        filter: 'agNumberColumnFilter',
        initialSort: 'asc',
        cellClass: 'font-medium',
      },
      {
        colId: 'missingFields',
        headerName: t('onFarmImplant.implantQueue.missingInfo'),
        initialWidth: 160,
        minWidth: 120,
        filter: 'agSetColumnFilter',
        valueGetter: (params) => {
          const labels = params.data ? missingLabels(params.data) : [];
          return labels.length ? labels.join(', ') : null;
        },
        cellRenderer: (
          params: CellRendererParams<OnFarmImplantQueueRecord>,
        ) => {
          if (!params.value) return null;
          const tooltip = t('onFarmImplant.implantQueue.missingTooltip', {
            fields: params.value as string,
          });
          return (
            <span
              className="inline-flex items-center gap-1 text-xs font-medium text-amber-700"
              title={tooltip}
              aria-label={tooltip}
            >
              <i className="pi pi-exclamation-triangle text-amber-500" />
              {params.value as string}
            </span>
          );
        },
      },
      {
        field: 'sourceType',
        colId: 'sourceType',
        headerName: t('onFarmImplant.implantQueue.sourceType'),
        initialFlex: 1,
        minWidth: 150,
        filter: 'agSetColumnFilter',
        valueGetter: (params) =>
          params.data ? sourceTypeLabel(params.data.sourceType) : null,
        cellRenderer: (params: { value?: string | null }) => {
          if (!params.value) return null;
          return (
            <StatusBadge label={params.value} colorMap={sourceTypeColors} />
          );
        },
      },
      {
        field: 'embryoName',
        colId: 'embryoName',
        headerName: t('onFarmImplant.implantQueue.embryo'),
        initialFlex: 1.4,
        minWidth: 150,
        filter: 'agTextColumnFilter',
        cellRenderer: (params: { data?: OnFarmImplantQueueRecord }) => {
          if (!params.data) return null;
          return (
            <div>
              <div className="font-medium text-gray-900">
                {params.data.embryoName ?? '\u2014'}
              </div>
              <div className="text-xs text-gray-500">
                {params.data.recordName ?? ''}
              </div>
            </div>
          );
        },
      },
      {
        field: 'dairyCompId',
        colId: 'dairyCompId',
        headerName: fieldHeader('dairyCompId'),
        initialWidth: 160,
        minWidth: 150,
        editable: isCellEditable,
        cellClassRules: editableCellClassRules,
        ...undoEditor('dairyCompId', { inputType: 'number', min: 1 }),
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'recipientTag',
        colId: 'recipientTag',
        headerName: fieldHeader('recipientTag'),
        initialFlex: 1,
        minWidth: 170,
        editable: isCellEditable,
        cellClassRules: editableCellClassRules,
        ...undoEditor('recipientTag', {
          inputType: 'text',
          maxLength: IMPLANT_QUEUE_TEXT_LIMITS.recipientTag,
        }),
        filter: 'agTextColumnFilter',
      },
      {
        field: 'implantDate',
        colId: 'implantDate',
        headerName: fieldHeader('implantDate'),
        initialWidth: 160,
        minWidth: 150,
        editable: isCellEditable,
        cellClassRules: editableCellClassRules,
        valueGetter: (params) => params.data?.implantDate?.slice(0, 10) ?? null,
        valueFormatter: (params) =>
          formatField(
            'implantDate',
            params.value as OnFarmImplantQueueFieldValue | undefined,
          ),
        ...undoEditor('implantDate', { inputType: 'date' }),
        filter: 'agSetColumnFilter',
      },
      selectColumn('implantType', options.implantTypeOptions),
      {
        field: 'clSide',
        colId: 'clSide',
        headerName: fieldHeader('clSide'),
        initialWidth: 170,
        minWidth: 160,
        editable: false,
        valueFormatter: (params) =>
          formatField(
            'clSide',
            params.value as OnFarmImplantQueueFieldValue | undefined,
            params.data,
          ),
        filter: 'agSetColumnFilter',
        filterValueGetter: (params: { data?: OnFarmImplantQueueRecord }) => {
          const value = params.data?.clSide;
          return value === null || value === undefined
            ? null
            : formatField('clSide', value, params.data);
        },
        cellRenderer: (
          params: CellRendererParams<OnFarmImplantQueueRecord>,
        ) => {
          const row = params.data;
          if (!row) return null;
          return (
            <span className="flex w-full items-center justify-between gap-1">
              <ClSideToggle
                value={row.clSide}
                options={options.clSideOptions}
                label={fieldHeader('clSide')}
                disabled={!isCellEditable({ data: row })}
                onChange={(value) =>
                  callbacks.get().onFieldChange?.(row, 'clSide', value)
                }
              />
              {undoButton(row, 'clSide')}
            </span>
          );
        },
      },
      {
        field: 'quality',
        colId: 'quality',
        headerName: fieldHeader('quality'),
        initialWidth: 140,
        minWidth: 130,
        editable: isCellEditable,
        cellClassRules: editableCellClassRules,
        ...undoEditor('quality', { inputType: 'number', min: 0 }),
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'distance',
        colId: 'distance',
        headerName: fieldHeader('distance'),
        initialWidth: 140,
        minWidth: 130,
        editable: isCellEditable,
        cellClassRules: editableCellClassRules,
        ...undoEditor('distance', { inputType: 'number', min: 0 }),
        filter: 'agNumberColumnFilter',
      },
      selectColumn('implantTechId', options.techOptions),
      selectColumn('farmLocationId', options.farmLocationOptions),
      {
        field: 'transferComment',
        colId: 'transferComment',
        headerName: fieldHeader('transferComment'),
        initialFlex: 1.4,
        minWidth: 180,
        editable: isCellEditable,
        cellClassRules: editableCellClassRules,
        ...undoEditor('transferComment', {
          inputType: 'text',
          maxLength: IMPLANT_QUEUE_TEXT_LIMITS.transferComment,
        }),
        filter: 'agTextColumnFilter',
      },
      {
        field: 'accountName',
        colId: 'accountName',
        headerName: t('onFarmImplant.implantQueue.account'),
        initialFlex: 1.2,
        minWidth: 130,
        filter: 'agSetColumnFilter',
      },
      {
        field: 'transferStrawNumber',
        colId: 'transferStrawNumber',
        headerName: t('onFarmImplant.implantQueue.transferStraw'),
        initialFlex: 0.8,
        minWidth: 120,
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'frozenStrawNumber',
        colId: 'frozenStrawNumber',
        headerName: t('onFarmImplant.implantQueue.frozenStraw'),
        initialFlex: 0.8,
        minWidth: 110,
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'cane',
        colId: 'cane',
        headerName: t('onFarmImplant.implantQueue.cane'),
        initialFlex: 0.6,
        minWidth: 80,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'stage',
        colId: 'stage',
        headerName: t('onFarmImplant.implantQueue.stage'),
        initialFlex: 0.6,
        minWidth: 80,
        filter: 'agNumberColumnFilter',
        valueFormatter: ({ value, data }) =>
          value == null && data?.sourceType === 'fresh'
            ? t('onFarmImplant.fresh.unknownStage')
            : (value ?? ''),
      },
      {
        field: 'grade',
        colId: 'grade',
        headerName: t('onFarmImplant.implantQueue.grade'),
        initialFlex: 0.6,
        minWidth: 80,
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'freezeMethodName',
        colId: 'freezeMethodName',
        headerName: t('onFarmImplant.implantQueue.freezeMethod'),
        initialFlex: 1,
        minWidth: 130,
        filter: 'agSetColumnFilter',
      },
      {
        field: 'embryoStatusName',
        colId: 'embryoStatusName',
        headerName: t('onFarmImplant.implantQueue.embryoStatus'),
        initialFlex: 0.9,
        minWidth: 120,
        filter: 'agSetColumnFilter',
        cellRenderer: (params: { value?: string }) => {
          if (!params.value) return null;
          return <StatusBadge label={params.value} colorMap={statusColors} />;
        },
      },
    ];

    return columns;
  }, [callbacks, formatField, options, readOnly, sourceTypeLabel, t]);
};
